import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { ProviderOperationIdsSchema } from './integrations/integration.models.operations';
import {
  DateTimeTzSchema,
  GenericAccountId,
  GenericConnectionId,
  GenericSubscriptionId,
  GenericUUID,
} from './models.generic';

extendZodWithOpenApi(z);

/**
 * Connection Activity
 * @warning This is super raw and includes a bunch of stuff we probably don't need on API.
 *          BUT, we're just straight-porting now for Experimental Days.
 *          @ref `useConnectionActivities.ts`
 * @todo Rename PresentableConnectionActivity
 * @todo Clean up and consolidate with /models/integrations/integration.models.operations.ts
 */
export type ConnectionActivity = z.infer<typeof ConnectionActivitySchema>;
export const ConnectionActivitySchema = z.object({
  id: GenericUUID.nullable().openapi({
    description: 'Connection Activity ID',
    example: '123e4567-e89b-12d3-a456-426614174000',
  }),
  metadata: z.object({
    when: z
      .string()
      .datetime({ offset: true })
      .optional()
      .openapi({ description: 'When the activity occurred', example: '2024-04-03T22:44:47.308Z' }),
    error: z
      .boolean()
      .or(z.string())
      .optional()
      .openapi({ description: 'If the activity had an error', example: false }),
    lastXhr: z
      .object({
        url: z.string().openapi({
          description: 'The last URL called on this operation',
          example: 'https://api.github.com/user/192263/events?page=6&per_page=50',
        }),
        eTag: z.string().nullable().openapi({
          example: 'W/"a22de8bd43272666201d5e2b542d8440b865ab6048835bc5e4565597f9061e5d"',
        }),
        when: z
          .string()
          .datetime({ offset: true })
          .openapi({ example: '2024-04-03T22:44:47.033Z' }),
        eTags: z.array(z.string()).openapi({
          example: ['W/"e68c95c7c698f6c4fb99eeeeb9a7f133c602aceb93ac78aa2fec6249d8fa84a4"'],
        }),
        status: z.number().int().openapi({ example: 200 }),
        rateLimit: z.string().or(z.number()).nullable().openapi({ example: '5000' }),
        lastModified: z
          .string()
          .datetime({ offset: true })
          .openapi({ example: '2024-03-21T00:55:00.000Z' }),
        lastRecordId: z
          .union([z.string(), z.number(), z.null()])
          .openapi({ example: 36700263376, nullable: undefined }), // this forces compatibility with oapi linter.
        firstRecordId: z
          .union([z.string(), z.number(), z.null()])
          .openapi({ example: 37144920957, nullable: undefined }),
        rateLimitRemaining: z
          .union([z.string(), z.number(), z.null()])
          .openapi({ example: 4831, nullable: undefined }),
        firstFewRecordsFingerprint: z
          .string()
          .or(z.number())
          .optional()
          .openapi({ example: '37144920957-37144684260-37143452954' }),
      })
      .optional(),
    operationId: ProviderOperationIdsSchema.optional().openapi({ example: 'importMemories' }),
    executionSeconds: z.string().optional().openapi({ example: '1.745' }),
  }),
  created_at: z.string().datetime({ offset: true }).openapi({
    description: 'When the record was created',
    example: '2024-04-03T22:44:47.310999+00:00',
  }),
  connection_id: GenericConnectionId.openapi({ example: '123e4567-e89b-12d3-a456-426614174000' }),
  operation: ProviderOperationIdsSchema.openapi({ example: 'importMemories' }),
  success: z.boolean().openapi({ example: true }),
  account_id: GenericAccountId,
  subscription_id: z.string().or(z.number()).nullable().openapi({ example: null }),
  connections: z.object({
    tool_id: z.string().openapi({ example: 'github' }),
    managing_user_id: z.string().openapi({ example: '208848a9-34a1-456f-b659-32e1e8a631a0' }),
  }),
});

export type ConnectionActivitiesWrite = z.infer<typeof ConnectionActivitiesWriteSchema>;
export const ConnectionActivitiesWriteSchema = z.object({
  id: GenericUUID.optional(),
  metadata: z.record(z.unknown()).optional(),
  created_at: DateTimeTzSchema.optional(),
  operation: ProviderOperationIdsSchema.optional(),
  success: z.boolean().optional(),
  connection_id: GenericConnectionId.nullable(),
  account_id: GenericAccountId.nullable(),
  subscription_id: GenericSubscriptionId.nullable(),
});
